import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import PlatformIcon from '@/components/base/PlatformIcon';
import type { CreatorProfile } from '@/types/portal';
import { formatCompact, formatMoney } from '@/utils/format';

interface ProfileHeaderProps {
  creator: CreatorProfile;
  shortlisted: boolean;
  onShortlist: () => void;
  onInvite: () => void;
  onReject: () => void;
}

export default function ProfileHeader({
  creator,
  shortlisted,
  onShortlist,
  onInvite,
  onReject,
}: ProfileHeaderProps) {
  const stats = [
    { label: 'Followers', value: formatCompact(creator.followers) },
    { label: 'Engagement', value: `${creator.engagementRate}%` },
    { label: 'Est. rate', value: creator.rate != null ? formatMoney(creator.rate, creator.currency) : '—' },
    {
      label: 'Rating',
      value: creator.agencyHistory.rating != null ? `${creator.agencyHistory.rating} / 5` : '—',
    },
  ];

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-4">
          <Avatar src={creator.avatar} alt={creator.name} size="xl" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {creator.name}
              </h1>
              {creator.verification === 'Verified' ? (
                <span className="inline-flex items-center gap-1 text-xs font-label text-accent-700">
                  <i className="ri-verified-badge-fill text-[14px]" />
                  Verified
                </span>
              ) : (
                <Badge tone="outline">{creator.verification}</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-foreground-500">
              {[creator.handle, creator.city].filter(Boolean).join(' · ')}
            </p>
            {creator.platforms.length > 0 ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {creator.platforms.map((account) => (
                  <span
                    key={`${account.platform}-${account.handle}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-2.5 py-0.5 text-[11px]"
                  >
                    <PlatformIcon platform={account.platform} className="text-[13px]" />
                    <span className="font-label font-medium text-foreground-800">
                      {account.platform}
                    </span>
                    <span className="text-foreground-400">{formatCompact(account.followers)}</span>
                  </span>
                ))}
              </div>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {creator.categories.map((category) => (
                <Badge key={category} tone="secondary">
                  {category}
                </Badge>
              ))}
              {creator.languages.map((language) => (
                <Badge key={language} tone="outline" icon="ri-translate-2">
                  {language}
                </Badge>
              ))}
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {creator.bio}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-2 md:w-52">
          <Button
            variant={shortlisted ? 'secondary' : 'outline'}
            icon={shortlisted ? 'ri-check-line' : 'ri-focus-3-line'}
            onClick={onShortlist}
          >
            {shortlisted ? 'Shortlisted' : 'Add to shortlist'}
          </Button>
          <Button icon="ri-send-plane-line" onClick={onInvite}>
            Invite to campaign
          </Button>
          <Button variant="ghost" icon="ri-close-line" onClick={onReject}>
            Not a fit
          </Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-background-100 pt-5 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-md bg-background-100 px-3 py-2.5">
            <p className="text-[11px] text-foreground-400">{stat.label}</p>
            <p className="mt-0.5 font-heading text-base font-semibold text-foreground-900">
              {stat.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}